import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, AlertCircle, Loader2, Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EASE } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical English values submitted to the API; display names are translated.
export const CATEGORIES = [
  "Pharmaceuticals",
  "Nutraceuticals",
  "Medical Devices",
  "Medical Consumables",
  "Herbal Products",
  "Ayurvedic Products",
  "Multiple / Other",
];

const INITIAL = {
  name: "",
  company: "",
  country: "",
  email: "",
  phone: "",
  product_category: "",
  requirement: "",
  quantity: "",
  destination_country: "",
  certifications: "",
  packaging: "",
  details: "",
};

const Field = ({ label, name, type = "text", required = false, value, onChange, error, textarea = false, testid }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={name} className="font-mono text-xs font-medium tracking-[0.14em] uppercase text-slate">
      {label} {required && <span className="text-pine">*</span>}
    </label>
    {textarea ? (
      <textarea
        id={name}
        name={name}
        rows={3}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="bg-transparent border-b border-ink/25 focus:border-ink outline-none py-2 text-base text-ink transition-colors duration-300 resize-y"
      />
    ) : (
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="bg-transparent border-b border-ink/25 focus:border-ink outline-none py-2 text-base text-ink transition-colors duration-300"
      />
    )}
    {error && (
      <p data-testid={`${testid}-error`} className="text-xs text-red-600 mt-0.5">
        {error}
      </p>
    )}
  </div>
);

export const EnquiryForm = ({ defaultCategory = "", defaultDestination = "" }) => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({ ...INITIAL, product_category: defaultCategory, destination_country: defaultDestination });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [reference, setReference] = useState(null);
  const [ackSent, setAckSent] = useState(false);

  const categoryNames = t("form.categories", { returnObjects: true });
  const categoryIndex = CATEGORIES.indexOf(form.product_category);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: undefined }));
  };

  const validate = () => {
    const er = {};
    ["name", "company", "country", "requirement"].forEach((k) => {
      if (!form[k].trim()) er[k] = t("form.errors.required");
    });
    if (!form.product_category) er.product_category = t("form.errors.category");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = t("form.errors.email");
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      const res = await axios.post(`${API}/enquiries`, { ...form, lang: (i18n.language || "en").slice(0, 2) });
      if (file) {
        const data = new FormData();
        data.append("file", file);
        try {
          await axios.post(`${API}/enquiries/${res.data.id}/attachment`, data);
        } catch {
          // enquiry is saved; attachment failure must not block the success state
        }
      }
      setReference(res.data?.id);
      setAckSent(Boolean(res.data?.ack_sent));
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="enquiry-form-wrapper" className="relative">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            data-testid="enquiry-success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="rounded border border-ice bg-white p-10 md:p-14"
          >
            <CheckCircle2 size={40} className="text-pine" strokeWidth={1.5} />
            <h3 className="font-display text-[26px] font-semibold text-ink mt-6">{t("form.successTitle")}</h3>
            <p className="text-slate mt-4 leading-relaxed max-w-md">
              {t(ackSent ? "form.successText" : "form.successTextNoAck", { name: form.name.split(" ")[0], email: form.email })}
            </p>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-slate mt-8" dir="ltr">
              {t("form.reference", { ref: reference })}
            </p>
            <button
              data-testid="enquiry-submit-another"
              onClick={() => { setForm(INITIAL); setStatus("idle"); setReference(null); }}
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-pine hover:text-ink transition-colors"
            >
              {t("form.submitAnother")} <ArrowRight size={15} className="rtl:-scale-x-100" />
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={onSubmit}
            noValidate
            data-testid="enquiry-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
          >
            <Field label={t("form.labels.name")} name="name" required value={form.name} onChange={onChange} error={errors.name} testid="enquiry-name-input" />
            <Field label={t("form.labels.company")} name="company" required value={form.company} onChange={onChange} error={errors.company} testid="enquiry-company-input" />
            <Field label={t("form.labels.country")} name="country" required value={form.country} onChange={onChange} error={errors.country} testid="enquiry-country-input" />
            <Field label={t("form.labels.email")} name="email" type="email" required value={form.email} onChange={onChange} error={errors.email} testid="enquiry-email-input" />
            <Field label={t("form.labels.phone")} name="phone" value={form.phone} onChange={onChange} testid="enquiry-phone-input" />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="product_category" className="font-mono text-xs font-medium tracking-[0.14em] uppercase text-slate">
                {t("form.labels.product_category")} <span className="text-pine">*</span>
              </label>
              <select
                id="product_category"
                name="product_category"
                value={categoryIndex === -1 ? "" : String(categoryIndex)}
                onChange={(e) => {
                  const idx = e.target.value;
                  setForm((f) => ({ ...f, product_category: idx === "" ? "" : CATEGORIES[Number(idx)] }));
                  setErrors((er) => ({ ...er, product_category: undefined }));
                }}
                data-testid="enquiry-category-select"
                className="bg-transparent border-b border-ink/25 focus:border-ink outline-none py-2 text-base text-ink transition-colors duration-300"
              >
                <option value="">{t("form.selectPlaceholder")}</option>
                {categoryNames.map((c, i) => (
                  <option key={CATEGORIES[i]} value={String(i)}>{c}</option>
                ))}
              </select>
              {errors.product_category && (
                <p data-testid="enquiry-category-select-error" className="text-xs text-red-600 mt-0.5">{errors.product_category}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Field label={t("form.labels.requirement")} name="requirement" required textarea value={form.requirement} onChange={onChange} error={errors.requirement} testid="enquiry-requirement-input" />
            </div>
            <Field label={t("form.labels.quantity")} name="quantity" value={form.quantity} onChange={onChange} testid="enquiry-quantity-input" />
            <Field label={t("form.labels.destination_country")} name="destination_country" value={form.destination_country} onChange={onChange} testid="enquiry-destination-input" />
            <Field label={t("form.labels.certifications")} name="certifications" value={form.certifications} onChange={onChange} testid="enquiry-certifications-input" />
            <Field label={t("form.labels.packaging")} name="packaging" value={form.packaging} onChange={onChange} testid="enquiry-packaging-input" />
            <div className="md:col-span-2">
              <Field label={t("form.labels.details")} name="details" textarea value={form.details} onChange={onChange} testid="enquiry-details-input" />
            </div>
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <label htmlFor="attachment" className="font-mono text-xs font-medium tracking-[0.14em] uppercase text-slate">
                {t("form.labels.upload")}
              </label>
              <label
                htmlFor="attachment"
                className="inline-flex items-center gap-3 border-b border-ink/25 py-2 text-sm text-slate cursor-pointer transition-colors duration-300 hover:border-ink"
              >
                <Paperclip size={15} className="text-pine" />
                <span data-testid="enquiry-upload-label">{file ? file.name : t("form.uploadPlaceholder")}</span>
              </label>
              <input
                id="attachment"
                name="attachment"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png"
                className="sr-only"
                data-testid="enquiry-upload-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>

            {status === "error" && (
              <div data-testid="enquiry-error" className="md:col-span-2 flex items-center gap-3 text-red-600 text-sm border border-red-200 bg-red-50 px-4 py-3">
                <AlertCircle size={18} />
                {t("form.error")}
              </div>
            )}

            <div className="md:col-span-2 flex flex-col gap-5 pt-2">
              <button
                type="submit"
                disabled={status === "submitting"}
                data-testid="enquiry-submit-button"
                className="inline-flex items-center justify-center gap-2 bg-ink text-white font-semibold px-8 py-4 rounded text-sm transition-colors duration-300 hover:bg-deep disabled:opacity-60 disabled:pointer-events-none w-fit"
              >
                {status === "submitting" ? (
                  <>{t("form.submitting")} <Loader2 size={16} className="animate-spin" /></>
                ) : (
                  <>{t("form.submit")} <ArrowRight size={16} className="rtl:-scale-x-100" /></>
                )}
              </button>
              <p className="text-xs text-slate leading-relaxed max-w-lg">{t("form.reassurance")}</p>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EnquiryForm;
